import { api } from "@/lib/api";

/** Journey API 错误（c456-journey 返回 `{ error: string }`） */
export class JourneyApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly payload: unknown,
  ) {
    super(extractJourneyErrorMessage(payload) ?? `请求失败（${status}）`);
    this.name = "JourneyApiError";
  }
}

export type JourneyInputMode = "form" | "freeform";
export type JourneyChannelPreference = "wecom" | "sms" | "email";

export type JourneyNode = {
  id?: number;
  input_mode: JourneyInputMode;
  offset_days: number;
  trigger?: string | null;
  channel_preference?: JourneyChannelPreference | null;
  instruction: string;
  prompt?: string | null;
  /** 该节点发送时段开始 HH:MM */
  send_window_start: string;
  /** 该节点发送时段结束 HH:MM（须晚于开始） */
  send_window_end: string;
};

export type JourneyDefinition = {
  id: number;
  account_id: number;
  brand_id: number;
  name: string;
  status: "draft" | "enabled" | string;
  business_wiki_id: number | null;
  /** 调度时区，当前固定 Asia/Shanghai */
  timezone: string;
  nodes: JourneyNode[];
  created_at: string;
  updated_at: string;
};

export type JourneyNodeInput = Omit<JourneyNode, "id">;

/** 默认发送时段 */
export const DEFAULT_SEND_WINDOW_START = "09:00";
export const DEFAULT_SEND_WINDOW_END = "18:00";

const HHMM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

/** 规范化为 HH:MM（兼容 input[type=time] 的 HH:MM:SS） */
export function normalizeHhmm(value: string): string {
  const trimmed = value.trim();
  if (HHMM_RE.test(trimmed)) return trimmed;
  const match = trimmed.match(/^([01]\d|2[0-3]):([0-5]\d)(?::[0-5]\d)?$/);
  if (!match) return trimmed;
  return `${match[1]}:${match[2]}`;
}

/** 前端校验发送时段；合法返回 null，否则返回中文错误 */
export function validateSendWindow(start: string, end: string): string | null {
  const s = normalizeHhmm(start);
  const e = normalizeHhmm(end);
  if (!HHMM_RE.test(s) || !HHMM_RE.test(e)) {
    return "发送时段须为 HH:MM 格式";
  }
  const toMinutes = (hhmm: string) => {
    const [h, m] = hhmm.split(":").map(Number);
    return h * 60 + m;
  };
  if (toMinutes(e) <= toMinutes(s)) {
    return "结束时间须晚于开始时间";
  }
  return null;
}

function journeyBaseUrl(): string {
  const raw = import.meta.env.VITE_JOURNEY_API_URL as string | undefined;
  const base = (raw ?? "http://localhost:3010").replace(/\/$/, "");
  return base;
}

function resolveJourneyUrl(path: string): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${journeyBaseUrl()}${normalized.startsWith("/api/") ? normalized : `/api/v1${normalized}`}`;
}

function extractJourneyErrorMessage(payload: unknown): string | undefined {
  if (typeof payload !== "object" || payload === null || !("error" in payload)) return undefined;
  const error = (payload as { error: unknown }).error;
  if (typeof error === "string") return error;
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message: unknown }).message === "string"
  ) {
    return (error as { message: string }).message;
  }
  return undefined;
}

async function parseResponse(response: Response): Promise<unknown> {
  if (response.status === 204) return undefined;
  const contentType = response.headers.get("content-type") ?? "";
  const text = await response.text();
  if (!text) return undefined;
  if (contentType.includes("application/json")) {
    try {
      return JSON.parse(text) as unknown;
    } catch {
      return text;
    }
  }
  return text;
}

/** 带账户 JWT 调 c456-journey；供 bindings 等扩展复用 */
export async function journeyRequest<T>(
  accountId: string | number,
  path: string,
  options: RequestInit = {},
  retried = false,
): Promise<T> {
  const headers = new Headers(options.headers);
  const accessToken = api.getAccessToken();
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
  headers.set("X-Account-Id", String(accountId));
  if (options.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const response = await window.fetch(resolveJourneyUrl(path), { ...options, headers });
  const payload = await parseResponse(response);

  if (response.status === 401 && !retried && (await api.refreshAccessToken())) {
    return journeyRequest<T>(accountId, path, options, true);
  }

  if (response.status === 401) {
    api.clearTokens();
    if (window.location.pathname !== "/login") {
      window.location.assign("/login");
    }
  }

  if (!response.ok) throw new JourneyApiError(response.status, payload);
  return payload as T;
}

/** 列出品牌下的旅程 */
export function listJourneys(accountId: string | number, brandId: string | number) {
  return journeyRequest<{ journeys: JourneyDefinition[] }>(
    accountId,
    `/journeys?brand_id=${encodeURIComponent(String(brandId))}`,
  );
}

/** 读取单个旅程 */
export function getJourney(accountId: string | number, journeyId: string | number) {
  return journeyRequest<JourneyDefinition>(accountId, `/journeys/${journeyId}`);
}

/** 创建草稿旅程 */
export function createJourney(
  accountId: string | number,
  body: {
    brand_id: number | string;
    name: string;
    nodes: JourneyNodeInput[];
    business_wiki_id?: number | null;
  },
) {
  return journeyRequest<JourneyDefinition>(accountId, "/journeys", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** 更新旅程（名称 / 节点 / 业务库） */
export function updateJourney(
  accountId: string | number,
  journeyId: string | number,
  body: {
    name?: string;
    nodes?: JourneyNodeInput[];
    business_wiki_id?: number | null;
  },
) {
  return journeyRequest<JourneyDefinition>(accountId, `/journeys/${journeyId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

/** 启用旅程（未就绪时 API 返回 422） */
export function enableJourney(accountId: string | number, journeyId: string | number) {
  return journeyRequest<JourneyDefinition>(accountId, `/journeys/${journeyId}/enable`, {
    method: "POST",
  });
}

/** 关闭旅程（回到 draft） */
export function disableJourney(accountId: string | number, journeyId: string | number) {
  return journeyRequest<JourneyDefinition>(accountId, `/journeys/${journeyId}/disable`, {
    method: "POST",
  });
}

/** 删除旅程（仅草稿；已启用须先关闭，否则 422） */
export function deleteJourney(accountId: string | number, journeyId: string | number) {
  return journeyRequest<void>(accountId, `/journeys/${journeyId}`, {
    method: "DELETE",
  });
}
